import { useState } from "react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CountryPicker } from "@/components/CountryPicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { toastApiError, toastApiSuccess } from "@/lib/api-toast";

interface CreateCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export function CreateCompanyDialog({ open, onOpenChange, onCreated }: CreateCompanyDialogProps) {
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("GB");
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setName("");
    setContactEmail("");
    setAddressLine1("");
    setCity("");
    setPostalCode("");
    setCountry("GB");
    setSubmitting(false);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { error, response } = await api.POST("/v1/companies", {
        body: {
          name: name.trim(),
          contactEmail: contactEmail.trim() || undefined,
          addressLine1: addressLine1.trim() || undefined,
          city: city.trim() || undefined,
          postalCode: postalCode.trim() || undefined,
          country: country || undefined,
        },
      });
      if (error) {
        await toastApiError({ error, response }, "Couldn't create the company.");
        return;
      }
      toastApiSuccess("Company created");
      onCreated();
      handleOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>New company</DialogTitle>
            <DialogDescription>
              A company groups the organisations of one multi-site business, so bills addressed to the
              group can be received and then routed to the right site.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="company-name">Name</Label>
              <Input
                id="company-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ozone Coffee"
                required
                autoFocus
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="company-contact-email">Contact email</Label>
              <Input
                id="company-contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="ap@ozonecoffee.com"
              />
              <p className="text-xs text-muted-foreground">
                Its domain is allowed to forward bills to this company straight away. Everyone else has
                to be allowed explicitly.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="company-address">Head office address</Label>
              <Input
                id="company-address"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                placeholder="11 Leonard Street"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="London" />
                <Input
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="EC2A 4AQ"
                />
              </div>
              <CountryPicker value={country} onChange={setCountry} />
              <p className="text-xs text-muted-foreground">
                Worth filling in. Bills delivered to head office are group costs rather than one site's,
                so knowing this address stops them being assigned to whichever site happens to share it.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name.trim()}>
              {submitting && <Spinner className="size-3.5" />} Create company
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
